import { ref } from 'vue';
import dataUnitsApi from '@/api/modules/dataUnitsApi';
import type { DataUnitPriceItem } from '@/types/billing';
import type { DataUnitProductKey } from '@/constants/dataUnitProducts';
import { formatBilledAmount } from '@/utils/billingPresentation';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

export const useDataUnitPrices = () => {
  const pricesByKey = ref<Partial<Record<DataUnitProductKey, DataUnitPriceItem>>>({});
  const loading = ref(false);
  const error = ref<string | null>(null);

  const loadDataUnitPrices = async (keys: DataUnitProductKey[]) => {
    const uniqueKeys = [...new Set(keys)];
    const missingKeys = uniqueKeys.filter((key) => !pricesByKey.value[key]);

    if (missingKeys.length === 0) return;

    loading.value = true;
    error.value = null;

    try {
      const response = await dataUnitsApi.getDataUnitPrices(missingKeys);
      const fetchedPrices = Object.fromEntries(
        response.items.map((item) => [item.key, item]),
      ) as Partial<Record<DataUnitProductKey, DataUnitPriceItem>>;

      pricesByKey.value = {
        ...pricesByKey.value,
        ...fetchedPrices,
      };
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Não foi possível carregar o preço dos dados.');
    } finally {
      loading.value = false;
    }
  };

  const getDataUnitPrice = (key: DataUnitProductKey | null) => {
    if (!key) return null;
    return pricesByKey.value[key] ?? null;
  };

  const getDataUnitPriceLabel = (key: DataUnitProductKey | null) => {
    const price = getDataUnitPrice(key);
    if (!price) return null;
    return formatBilledAmount(price.amount, price.currency);
  };

  return {
    pricesByKey,
    loading,
    error,
    loadDataUnitPrices,
    getDataUnitPrice,
    getDataUnitPriceLabel,
  };
};
